import Entity from "_framework/entities/entity";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import UserEntityMemberInvitationSchema, {
  UserEntityMemberInvitationJSON,
  UserEntityMemberInvitationParams,
} from "legal-entities/invitations/member-invitations/email-based/_schema/user-entity-member-invitation-schema";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import RegistrationInfo from "legal-entities/registration-info/registration-info";
import UserEntityInfo from "users/information/entity-information/user-entity-info";

export default class UserEntityMemberInvitation extends Entity<
  UserEntityMemberInvitation,
  UserEntityMemberInvitationJSON,
  UserEntityMemberInvitationParams
> {
  public static readonly EXAMPLE: UserEntityMemberInvitation = UserEntityMemberInvitation.create({
    id: Guid.generate(),
    userEntityInfo: UserEntityInfo.EXAMPLE,
    registrationInfo: RegistrationInfo.EXAMPLE,
    status: InvitationStatus.Open,
    expires: AHDate.TODAY.add(7, "day"),
  });

  public static create(values: UserEntityMemberInvitationParams): UserEntityMemberInvitation {
    const parsed = UserEntityMemberInvitationSchema.validate(values);
    return new UserEntityMemberInvitation(parsed);
  }

  public static fromJSON(json: unknown): UserEntityMemberInvitation | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const params = UserEntityMemberInvitationSchema.deserialize(json);
    return new UserEntityMemberInvitation(params);
  }

  public static fromJSONArray(jsonArray: UserEntityMemberInvitationJSON[]): UserEntityMemberInvitation[] {
    return jsonArray
      .map((json) => UserEntityMemberInvitation.fromJSON(json))
      .filter((invitation): invitation is UserEntityMemberInvitation => !isNullOrUndefined(invitation));
  }

  public readonly id: Guid;
  public readonly userEntityInfo: UserEntityInfo;

  public readonly registrationInfo: RegistrationInfo;
  public readonly status: InvitationStatus;
  public readonly expires?: AHDate;
  /** When the invitation letter went. Absent for one sent before that was recorded. */
  public readonly sentDate?: AHDate;

  public constructor(params: UserEntityMemberInvitationParams) {
    super(params);
    this.id = params.id ?? Guid.generate();
    this.userEntityInfo = params.userEntityInfo;
    this.registrationInfo = RegistrationInfo.create({ params: params.registrationInfo });
    this.status = params.status ?? InvitationStatus.Open;
    this.expires = params.expires;
    this.sentDate = params.sentDate;
  }

  public async accept(): Promise<void> {
    if (this.status !== InvitationStatus.Open) {
      throw new InvalidUserEntityMemberInvitationStatusError("Cannot accept an invitation that is not open.");
    }
  }

  public toJSON(): UserEntityMemberInvitationJSON {
    return UserEntityMemberInvitationSchema.serialize(this);
  }

  public valueOf(): UserEntityMemberInvitationJSON {
    return this.toJSON();
  }

  public isEqualTo(other: UserEntityMemberInvitation | null | undefined): boolean {
    return this.id?.isEqualTo(other?.id ?? null) ?? false;
  }

  public toString(): string {
    return `UserEntityMemberInvitation {
      id: ${this.id?.value ?? "undefined"},
      entity: ${this.userEntityInfo.toString()},
      registrationInfo: ${this.registrationInfo?.toString() ?? "undefined"},
      status: ${this.status}
    }`;
  }
}

export class UserMemberInvitationNotFoundError extends Error {
  constructor(id: Guid) {
    super(`Member invitation with id ${id} not found.`);
  }
}
export class InvalidUserEntityMemberInvitationStatusError extends Error {}
export class InvalidUserEntityMemberInvitationError extends Error {}
/**
 * The link is missing its acceptance token or carries the wrong one - typed from somewhere, or sent
 * before links carried tokens. The invitee can have a fresh link emailed to them.
 */
export class InvitationLinkInvalidError extends Error {
  constructor(message = "This invitation link can no longer be used. We can email you a fresh one.") {
    super(message);
    this.name = "InvitationLinkInvalidError";
  }
}

/** The invitee already has an account, so they accept by signing into it. */
export class InvitationRequiresSignInError extends Error {
  constructor(message = "You already have an Attorney Hub account. Sign in to accept this invitation.") {
    super(message);
    this.name = "InvitationRequiresSignInError";
  }
}

/** Somebody is signed in, and the invitation was sent to somebody else. */
export class InvitationForAnotherAccountError extends Error {
  constructor(message = "This invitation is for somebody else. Sign out, then open it again.") {
    super(message);
    this.name = "InvitationForAnotherAccountError";
  }
}

export class MemberInvitationNotOpenError extends Error {
  constructor(message = "This member invitation is no longer open. Ask an administrator for a new invitation and try again.") {
    super(message);
  }
}
